import { Sector } from '@/domain/impact/impact.entity';
import {
  Category,
  EnergyType,
  EnergyTypeValues,
  ImpactCo2Answer,
  ValuesEnergy,
} from '@/domain/impact-co2/entities/question.entity';
import { validateValueInType } from '@/domain/utils/validate-value-in-type';

import { ImportedRow } from '../convert-excel-to-impact-answers';
import { buildBaseAnswer } from './common/build-base-answer';
import {
  filterRowsByCategory,
  filterRowsBySectors,
} from './common/filter-rows-by-category';
import { ImportMappingError } from './common/import-mapping.error';

const CATEGORY: Category = 'ENERGY';
const SECTORS: Sector[] = ['SITE'];

export function mapToEnergyAnswers(
  rows: ImportedRow[],
): ImpactCo2Answer<ValuesEnergy>[] {
  return rows
    .filter(filterRowsByCategory(CATEGORY))
    .filter(filterRowsBySectors(SECTORS))
    .map((row) => buildAnswer(row));
}

function buildAnswer(row: ImportedRow) {
  const energyType = mapToEnergyType(row);
  const energyConsumptionQuantity = mapToEnergyConsumptionQuantity(
    row,
    energyType,
  );
  const baseAnswer = buildBaseAnswer(row, CATEGORY);
  const answer = {
    ...baseAnswer,
    values: {
      ...baseAnswer.values,
      energyType,
      energyConsumptionQuantity,
    },
  };
  return answer;
}

function mapToEnergyType(row: ImportedRow): EnergyType {
  const tgsValue = row['Fairly Sub Category Description'];
  const result = validateValueInType<EnergyType>(tgsValue, EnergyTypeValues);
  if (!result) {
    throw new ImportMappingError('energyType', tgsValue, row);
  }
  return result;
}

/**
 * Can be kwh or liter depending on the energy type
 */
function mapToEnergyConsumptionQuantity(
  row: ImportedRow,
  energyType: EnergyType,
): number {
  const columnName = getColumnNameForEnergyType(energyType);
  const tgsValue = row[columnName];
  const result = parseFloat(tgsValue);
  if (isNaN(result)) {
    throw new ImportMappingError('energyConsumptionQuantity', tgsValue, row);
  }
  return result;
}

function getColumnNameForEnergyType(
  energyType: EnergyType,
): 'Elec Consumption (kwh)' | 'Volume (L)' {
  switch (energyType) {
    case 'ELECTRICITY_NETWORK_FRANCE':
    case 'ELECTRICITY_NETWORK_OUTSIDE_FRANCE':
    case 'PHOTOVOLTAIC_ELECTRICITY':
      return 'Elec Consumption (kwh)';
    case 'GAS':
    case 'FUEL_OIL':
    case 'LOGS':
    case 'WOOD_PELLETS':
    case 'WOOD':
      return 'Volume (L)';
    default:
      throw Error('Unhandled energy type: ' + energyType);
  }
}
